/**
 * Block **state** — the number in a chunk means *which block, in which state*.
 *
 * **T108 · D47 · [ADR-0009](../../../Project/adr/0009-block-state-and-per-block-data.md)**
 *
 * ## What this is, in one paragraph
 *
 * A chunk is a `Uint16Array`: 32,768 slots, each an unsigned 16-bit number.
 * That number used to be a block id and is now a **state id**. `eldstaedi` is
 * one block with two states — lit and unlit — and they are two different
 * numbers. Nothing about the storage format changes: same array, same 64 KB
 * frame, same sparse `[slot, number]` deltas, same frozen-version rule. What
 * changes is what the number is understood to mean, and that this file hands
 * the numbers out.
 *
 * ## 🔴 The two rules that keep saved worlds working
 *
 * **1. Blocks own 0…1023. States own 1024…65535.** A fixed boundary, not a
 * shared counter. `palette-lock.json` appends blocks and `state-lock.json`
 * appends states, and because the two ranges cannot meet, neither file has to
 * know the other exists. `isStateId` is one comparison, which matters because
 * the mesher asks it once per block per remesh.
 *
 * **2. The all-defaults combination IS the base block's own id.** `eldstaedi`
 * is 34, and `eldstaedi[lit=false]` is also 34 — not a new number. That is what
 * makes ADR-0009's promise true: every hearth anybody has already placed is
 * still the number it always was, and still means an unlit hearth. ⚠️ **The
 * corollary is a live hazard:** reordering a property's values in
 * `block-states.json` changes which combination is the default, and therefore
 * silently changes what every saved chunk means. A test pins every default.
 *
 * ## What does NOT belong here
 *
 * Anything unbounded. The hearth's nine slots, how much fuel is left, and when
 * it was lit are **rows in the side table**, keyed by world position — ADR-0009
 * §2. ⚠️ **Getting this wrong in the cheap direction is silent**: a thing that
 * should have been a row, made into a state, works perfectly until the numbers
 * run out. The question to ask is *"how many possible values, exactly?"* and if
 * the answer is not a small finite number written down in `block-states.json`,
 * it is a row.
 *
 * ## ⚠️ This module deliberately does not import `blocks.ts`
 *
 * It reads the lock files directly. `blocks.ts` imports *this*, so that
 * `blockById(1024)` can answer with the hearth — the seam that lets every
 * existing accessor keep working without knowing states exist. Importing
 * `blocks.ts` back would make that a cycle.
 */

import statesData from '../data/block-states.json' with { type: 'json' };
import stateLock from '../data/state-lock.json' with { type: 'json' };
import paletteLock from '../data/palette-lock.json' with { type: 'json' };

/**
 * 🔴 **The boundary between block ids and state ids.**
 *
 * Blocks are `0 … STATE_ID_BASE - 1`. States are `STATE_ID_BASE … 65535`.
 * See the module comment for why this is fixed rather than a running counter.
 */
export const STATE_ID_BASE = 1024;

/**
 * ⚠️ **The real ceiling, and it is load-bearing.**
 *
 * A chunk slot is unsigned 16-bit, so 65,535 is the last usable number. ADR-0009
 * asks for the count to be *watched* rather than assumed — `tools/build-states.mjs`
 * prints it on every run, and {@link stateBudget} is the same number for anything
 * that wants to assert on it.
 */
export const STATE_ID_CEILING = 65_536;

/** What a property can be set to. Bounded, small, and written down by a human. */
export type StateValue = string | number | boolean;

/** Every property of one state, by name. */
export type BlockState = Readonly<Record<string, StateValue>>;

/**
 * 🔴 **What is true of ONE state and not of the others.**
 *
 * ⚠️ **This exists because `block-behaviour.json` and `block-appearance.json`
 * are keyed by block KEY, and lit and unlit share one key.** Writing `warms`
 * over there would be true of every hearth in the world including the ones that
 * have burned out — the fault `$lightIsAbsentEverywhere` describes, arriving
 * through a different door.
 *
 * 👉 **`light` is the next field to land here**, and it is deliberately absent:
 * `Development/Minecraft/14_Light.md` §7.6 says build the light engine against
 * something that never changes and only then let the hearth switch it on.
 */
export interface StateOverride {
  /** 🔥 Does standing near this warm you? {@link warmsFromWeather} in `shelter.ts`. */
  readonly warms?: boolean;
  /**
   * 🔦 How much light this state gives off, 0–15. {@link lightFrom} in `light.ts`.
   *
   * 🔴 **THIS IS THE FIELD `block-behaviour.json` PREDICTED IN WRITING, months
   * before anything needed it.** Its `$lightIsAbsentEverywhere` note worked out
   * that a lit hearth and a cold one share a block key, so a `light` written
   * there *"would glow on BOTH, which is the exact fault the sentences above
   * describe, arriving through a different door."*
   *
   * ⚠️ **A torch's light does NOT live here**, and the difference is the point:
   * every one of a torch's five states is lit, because it burns for ever, so its
   * number is a property of the BLOCK and belongs in `block-behaviour.json`.
   * **Only a thing that is sometimes lit and sometimes not belongs here.**
   */
  readonly light?: number;
  /**
   * 🚪 **Does this state stop you walking into it?** Overrides the block's own
   * `solid`. {@link isSolid} in `blocks.ts` reads it.
   *
   * 🔴 **The gate is why, D130**: a shut `hlid` is a wall and an open one is a
   * gap, under ONE block key — so, like the hearth's warmth, it can only be
   * said here. ⚠️ Everything that asks `isSolid` follows it for free: the
   * player's collision, a sheep's ground, light, what a torch may hang on.
   */
  readonly solid?: boolean;
}

interface AuthoredBlockStates {
  readonly properties: Readonly<Record<string, readonly StateValue[]>>;
  /** Keyed by the FULL state key — `"eldstaedi[lit=true]"`, never a bare block key. */
  readonly perState?: Readonly<Record<string, StateOverride>>;
  readonly note?: string;
}

/* Only the `blocks` object is data; every other key in the file is a `$`-prefixed
   note for the human reading it, exactly as block-behaviour.json does. */
const AUTHORED: ReadonlyMap<string, AuthoredBlockStates> = new Map(
  Object.entries(statesData.blocks as unknown as Record<string, AuthoredBlockStates>),
);

const BLOCK_IDS: ReadonlyMap<string, number> = new Map(Object.entries(paletteLock.ids));

const LOCKED: ReadonlyMap<string, number> = new Map(Object.entries(stateLock.ids));

/**
 * The canonical name of one state — `"eldstaedi[lit=true]"`.
 *
 * ⚠️ **This string is the identity, and the number is only a representation** —
 * the same rule the block palette lives by (T80). Properties are written in the
 * order they are declared in `block-states.json`, never sorted, because the
 * declaration order is also what decides the default.
 */
export function stateKey(blockKey: string, state: BlockState): string {
  const authored = AUTHORED.get(blockKey);
  if (authored === undefined) return blockKey;
  /* A Map rather than `state[name]`. Indexing a plain object with a string is
     the object-injection pattern the linter flags, and this file's whole job is
     turning authored names into numbers — so a Map removes the question instead
     of suppressing it, exactly as BY_KEY does in blocks.ts. */
  const values = new Map(Object.entries(state));
  const parts = Object.keys(authored.properties).map(
    (name) => `${name}=${String(values.get(name))}`,
  );
  return `${blockKey}[${parts.join(',')}]`;
}

/**
 * Every combination of one block's properties, in declaration order.
 *
 * Combination **0 is always the all-defaults one** — the first value of every
 * property — and that is what makes it the base block's own id.
 *
 * ⚠️ **A correction, because the first version of this comment overstated it.**
 * It said swapping these two loops would change what every saved chunk means.
 * It would not: index 0 is the all-defaults combination whichever way round they
 * go, and — more to the point — **a state's number comes from its NAME in the
 * lock file, never from its position in this list.** So the loop order decides
 * only the order in which brand-new states get appended by
 * `tools/build-states.mjs`. That is worth keeping stable, and it is not a
 * safety property. 👉 **The lock being keyed by name is the safety property**,
 * and it is a much stronger one than index order could ever be.
 *
 * ⚠️ **Exported only so the cross product can be tested before a second
 * property exists in the data.** Today one block has one property, so every
 * ordering produces the same list and the rule is untestable through the data
 * file. `kvika` and `vatn` both want a `level` 0–7, so the day this matters is
 * coming, and a test that only starts working then is a test nobody wrote.
 */
export function combinations(
  properties: Readonly<Record<string, readonly StateValue[]>>,
): BlockState[] {
  let out: BlockState[] = [{}];
  for (const [name, values] of Object.entries(properties)) {
    const next: BlockState[] = [];
    for (const partial of out) {
      for (const value of values) next.push({ ...partial, [name]: value });
    }
    out = next;
  }
  return out;
}

interface ResolvedState {
  readonly id: number;
  readonly blockKey: string;
  readonly blockId: number;
  readonly state: BlockState;
  readonly key: string;
}

const RESOLVED: ReadonlyMap<number, ResolvedState> = (() => {
  const out = new Map<number, ResolvedState>();
  for (const [blockKey, authored] of AUTHORED) {
    const blockId = BLOCK_IDS.get(blockKey);
    /* A state for a block that is not in the palette. Left silent rather than
       thrown so that a data mistake fails in the TEST, with a sentence saying
       what to do, rather than at import time in the middle of a game. */
    if (blockId === undefined) continue;
    const combos = combinations(authored.properties);
    for (const [index, state] of combos.entries()) {
      const key = stateKey(blockKey, state);
      /* Combination 0 is the all-defaults one and it is NOT a new number: it is
         the block's own id, unchanged since the day the block was added. */
      const id = index === 0 ? blockId : LOCKED.get(key);
      if (id === undefined) continue;
      out.set(id, { id, blockKey, blockId, state, key });
    }
  }
  return out;
})();

const BY_KEY: ReadonlyMap<string, ResolvedState> = new Map(
  [...RESOLVED.values()].map((s) => [s.key, s]),
);

/**
 * Every authored {@link StateOverride}, by the number it applies to.
 *
 * ⚠️ **Built by looking each authored state key UP in {@link BY_KEY}**, so an
 * override naming a state that does not exist lands nowhere at all. That is
 * silent here on purpose — the same rule the resolver above follows — and
 * `states.test.ts` is what makes it loud.
 */
const OVERRIDES: ReadonlyMap<number, StateOverride> = (() => {
  const out = new Map<number, StateOverride>();
  for (const authored of AUTHORED.values()) {
    for (const [stateKeyName, override] of Object.entries(authored.perState ?? {})) {
      const resolved = BY_KEY.get(stateKeyName);
      if (resolved === undefined) continue;
      out.set(resolved.id, override);
    }
  }
  return out;
})();

/**
 * 🔴 **What is true of this exact state and not of its siblings.**
 *
 * ⚠️ **Ask this, never `block-behaviour.json`, for anything a lit hearth has
 * and a cold one does not.** See {@link StateOverride}.
 */
export function stateOverride(id: number): StateOverride | undefined {
  return OVERRIDES.get(id);
}

/** Every authored override key, for the test that proves each one names a real state. */
export const AUTHORED_OVERRIDE_KEYS: readonly string[] = [...AUTHORED.values()].flatMap(
  (authored) => Object.keys(authored.perState ?? {}),
);

/**
 * Is this number a state rather than a plain block?
 *
 * 🔴 **One comparison on purpose.** The mesher asks this per block per remesh,
 * and the whole reason the number space is split at a fixed boundary rather
 * than shared is so that this never becomes a map lookup.
 */
export function isStateId(id: number): boolean {
  return id >= STATE_ID_BASE;
}

/**
 * The block a number belongs to — the seam everything else hangs off.
 *
 * A plain block id answers itself. A state id answers the block it is a state
 * *of*, so a lit hearth is still a hearth to everything that only wants to know
 * how hard it is to break, whether you can stand on it, or what it is called.
 *
 * ⚠️ **An unknown state answers `BLOCK_AIR`'s number, 0.** A number nobody has
 * ever handed out cannot be resolved, and answering 0 makes it *visibly*
 * nothing rather than quietly the wrong block — the same reasoning as the
 * magenta fallback in `blockAppearance`.
 */
export function baseBlockOf(id: number): number {
  if (!isStateId(id)) return id;
  return RESOLVED.get(id)?.blockId ?? 0;
}

/**
 * The property values a number stands for.
 *
 * A block with no states at all answers `{}` — it has no properties, which is a
 * different thing from having unknown ones, and callers can treat both the same
 * way because a missing property reads as `undefined` either way.
 */
export function stateOf(id: number): BlockState {
  return RESOLVED.get(id)?.state ?? {};
}

/** The canonical name of a number — `"eldstaedi[lit=true]"` — or `undefined`. */
export function stateKeyOf(id: number): string | undefined {
  return RESOLVED.get(id)?.key;
}

/**
 * The number for one block in one state, or `undefined` if there is no such state.
 *
 * ⚠️ **`changes` is merged over the number you pass**, so flipping one property
 * of a block that has several does not mean restating the rest:
 * `withState(hearthId, { lit: true })`.
 *
 * 🔴 **`undefined` means the combination was never locked**, which is either a
 * typo or a state somebody added to `block-states.json` without running
 * `tools/build-states.mjs`. Callers must not fall back to the base block — a
 * fire that silently stays unlit because a lookup missed is the shape of bug
 * this whole file exists to avoid. Let it fail loudly.
 */
export function withState(id: number, changes: BlockState): number | undefined {
  const blockId = baseBlockOf(id);
  /* 🔴 THE NUMBER PASSED FIRST, and the base block only if it is unknown.
     ⚠️ It was the other way round until 2026-09-29, which merged `changes`
     over the BASE block's state and threw the caller's other properties away —
     silent while every block with states had only one property, and found the day the gate
     (D130) became the first block with two: opening a south-facing gate
     handed back a NORTH-facing one. */
  const entry = RESOLVED.get(id) ?? RESOLVED.get(blockId);
  if (entry === undefined) return undefined;
  /**
   * 🔴 **A property this block does not have is a MISTAKE, not a no-op.**
   *
   * Found by its own test. `stateKey` only ever writes out the properties a
   * block declares, so `withState(hearth, { blazing: true })` used to serialise
   * to `eldstaedi[lit=false]` — a real, valid state — and hand back an UNLIT
   * hearth. A typed property name would have quietly put the fire out and
   * nothing would have failed.
   *
   * ⚠️ This is `edible.restoresPercent` again in a new costume: the call looked
   * like it worked and meant something else.
   */
  const properties = AUTHORED.get(entry.blockKey)?.properties ?? {};
  for (const name of Object.keys(changes)) {
    if (!Object.hasOwn(properties, name)) return undefined;
  }
  const wanted = { ...entry.state, ...changes };
  return BY_KEY.get(stateKey(entry.blockKey, wanted))?.id;
}

/** Every number this block can be, including its default. Declaration order. */
export function statesOf(blockKey: string): readonly number[] {
  const authored = AUTHORED.get(blockKey);
  const blockId = BLOCK_IDS.get(blockKey);
  if (authored === undefined || blockId === undefined) return [];
  return combinations(authored.properties)
    .map((state, index) => (index === 0 ? blockId : BY_KEY.get(stateKey(blockKey, state))?.id))
    .filter((id): id is number => id !== undefined);
}

/** Does this block have states at all? */
export function hasStates(blockKey: string): boolean {
  return AUTHORED.has(blockKey);
}

/**
 * How full the number space is — **the number ADR-0009 asks somebody to watch.**
 *
 * `used` counts only numbers above the boundary, because the block palette
 * spends from its own range and the two cannot compete.
 */
export function stateBudget(): { used: number; available: number; ceiling: number } {
  const used = LOCKED.size;
  return { used, available: STATE_ID_CEILING - STATE_ID_BASE - used, ceiling: STATE_ID_CEILING };
}

/** Every locked state key and its number — for the tools and the tests. */
export const LOCKED_STATES: ReadonlyMap<string, number> = LOCKED;

/**
 * 🔴 **A state key back to its number — the inverse of {@link stateKeyOf}.**
 *
 * **This exists because `block_update` carries a STRING, not a number.** The
 * wire has always named a block by its key, and `applyBlockUpdate` on the client
 * turns that key back into an id. A lit hearth has no plain block key of its
 * own, so without this the server could not tell anybody a fire had lit.
 *
 * ⚠️ **It answers plain block keys too** — `eldstaedi[lit=false]` is 34, the
 * hearth's own number — so a caller never has to know which kind of name it is
 * holding.
 *
 * 👉 **A key that names no state answers `undefined`**, and the client already
 * treats that as *"this build is older than the world it is looking at"* and
 * says so loudly. That was written for a different reason and turns out to be
 * exactly right here.
 */
export function stateIdForKey(key: string): number | undefined {
  return BY_KEY.get(key)?.id;
}

/** Every block that has states, and the properties it has. For the tools and the tests. */
export const AUTHORED_STATES: ReadonlyMap<string, AuthoredBlockStates> = AUTHORED;

/** Every combination one block can be in, in declaration order. For the tools. */
export function combinationsOf(blockKey: string): readonly BlockState[] {
  const authored = AUTHORED.get(blockKey);
  return authored === undefined ? [] : combinations(authored.properties);
}
/**
 * 🔴 **Did this edit replace the BLOCK, or only change its state?**
 *
 * **This is the question the side table's lifetime hangs on**, and it is not
 * *"did the number change"*.
 *
 * ADR-0009 §2: **breaking a block must delete its row.** A leaked row is
 * invisible — nothing lists it, nothing is harmed by it, and it does nothing at
 * all until somebody builds on the same square months later and finds it
 * already full of what the last block held.
 *
 * ⚠️ **But a hearth going from unlit to lit is a DIFFERENT NUMBER and must keep
 * its row.** **D86**: a cold hearth is still your home and still holds your
 * things, so the fire going out cannot empty it. `34 → 1024` is the same block
 * in a new state; `34 → 0` is the block being broken. **A test on the raw
 * numbers would get this exactly backwards.**
 *
 * 👉 **It has a name so it can be grepped**, which this repo has a scar about:
 * a rule that lived as an unnamed lambda in a raycast let three documents go on
 * saying the opposite for a week, because there was nothing to search for.
 */
export function changesBlock(before: number, after: number): boolean {
  return baseBlockOf(before) !== baseBlockOf(after);
}
